import React, { PropTypes } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { change as changeFieldValue } from "redux-form";

import * as dataActions from "../../../../utils/data/dataActions";
import {
  ACTIVE_BILLING_PERIODS,
  CLIENT_ATTACHMENTS,
  CLIENT_CONFIGURATION,
  CLIENT_FOLLOW_UPS,
  CLIENT_V2,
  FOLLOW_UPS_GRAPHQL,
  IS_MINT_ENABLED,
} from "../../../../utils/data/models";
import { FollowUps } from "./notes";
import { INCIDENT, SPECIAL_MODIFICATION } from "./notes/enums/followUpTypes";

import { AGENT, INTERNAL_ROLES } from "../../../../utils/Roles";
import { Render } from "../../../common";
import { RectangleSpinner } from "../../../common/spinners";
import ClientHeader from "./ClientHeader";
import ClientNav from "./ClientNav";

import { FormattedMessage } from "react-intl";
import { toast } from "react-toastify";
import { EN } from "../../../../components/common/enums/languages";
import userHasAccess from "../../../../utils/userHasAccess";
import {
  getParsedErrors,
  getSingleErrorFromCode,
  renderErrorModal,
} from "../../../common/form/FieldErrorHelper";
import { buildGraphQLQueryParams } from "../../../common/GraphQL/GraphQLQueryHelper";
import { isClientInTransition } from "../../../common/RouteValidators/ClientValidationHelper";
import enCA from "../../../intl/en-CA";
import frCA from "../../../intl/fr-CA";
import { cleanBrokerRelationshipsFromForm } from "../client-form/basic-client-sections/associatedBrokers";
import * as clientActions from "./clientActions";
import simpleFetch from "../../../../../utils/simpleFetch";

class ClientPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      submitData: true,
      showModal: false,
      errorsString: "",
      actionType: "",
      errorPath: "validation/clientFieldValidation/serverErrors",
      unauthorized: false,
      clientInTransition: undefined,
      automaticIdentifierConfigurationTypes: {}
    };
    this.createClientNote = this.createClientNote.bind(this);
    this.updateClient = this.updateClient.bind(this);
    this.updateClientNote = this.updateClientNote.bind(this);
    this.archiveButtonEdit = this.archiveButtonEdit.bind(this);
    this.updateClientInfo = this.updateClientInfo.bind(this);
    this.handleServerError = this.handleServerError.bind(this);
    this.showSingleModalError = this.showSingleModalError.bind(this);
    this.close = this.close.bind(this);
    this.updateTransition = this.updateTransition.bind(this);
    this.refetchIsTransition = this.refetchIsTransition.bind(this);
    this.clearContactInfo = this.clearContactInfo.bind(this);
    this.getGraphQLQuery = this.getGraphQLQuery.bind(this);
    this.getFollowUps = this.getFollowUps.bind(this);
  }

  componentDidMount() {
    this._initializePage(this.props.clientId, this.props.auth.user.userId);
    isClientInTransition(this.props.clientId).then((res) => {
      /** Response is either true or false. */
      this.updateTransition(res);
    });
  }

  componentWillUpdate(nextProps) {
    if (this.props.clientId !== nextProps.clientId) {
      this._initializePage(nextProps.clientId, nextProps.auth.user.userId);
    }
  }

  getFollowUps() {
    const { clientId } = this.props;
    const filter = `follow_ups.client_id==${clientId}`;
    const graphQLQuery = this.getGraphQLQuery(filter, "follow_ups.created_at:desc", 0, 200, "getFollowUps", "");
    this.props.dataActions
      .getGraphQL(FOLLOW_UPS_GRAPHQL, { query: graphQLQuery }, "");
  }

  getGraphQLQuery(filter, sortBy, offset, limit, queryName, q) {
    const colsStr = `
    followUpId
    priority
    createdAt
    transactionAmount
    updatedAt
    clientId
    memberId
    closedDate
    createdByUserId
    recipientUserId
    followUpCategoryId
    incidentType
    createdByUser {
        firstName
        lastName
      }
    closedByUser {
        firstName
        lastName
    }
    deadline
    description
    client {
        name
        isArchived
    }
    followUpType
    followUpOriginType
    member {
        memberId
        firstName
        lastName
        memberIdentifier
        isArchived
    }
    followUpStatusType
    descriptionKey
    recipientUser {
        firstName
        lastName
        emailAddress
      }
    relatedUser {
      firstName
      lastName
      emailAddress
      associatedClientId
    }
    commentsCount
    attachmentsCount
    followUpGuid
    specialModificationType
    `;
    return buildGraphQLQueryParams("followUps", filter, sortBy, offset, limit, colsStr, queryName, q, `sectionType: "MEMBERS"`);
  }

  _initializePage(clientId, userId) {
    const now = new Date().toISOString();
    this.props.dataActions.clearDataList(ACTIVE_BILLING_PERIODS);
    this.props.dataActions.clearDataCount(CLIENT_ATTACHMENTS);
    if (/^\d+$/.test(clientId)) {
      this.props.dataActions.getDataItem(CLIENT_V2, clientId).then((res) => {
      if (res.payload.error && res.payload.error.status === 401) {
        this.setState({ unauthorized: true });
        toast(
          <FormattedMessage id="clients.unauthorizedExternalUser" />,
          {
            type: toast.TYPE.ERROR,
            autoClose: false,
          }
        );
      }
      if (userHasAccess(AGENT)) {
        this.getFollowUps();
        dataActions.getDataCount(
          CLIENT_FOLLOW_UPS,
          [
            `followUps.clientId==${clientId};followUps.recipientUserId!=null;followUps.followUpStatusType==OPEN;followUps.followUpOriginType!=SYSTEM`,
          ],
          "MEMBER"
        );
      }});
      this.props.dataActions.getDataList(
        ACTIVE_BILLING_PERIODS,
        0,
        0,
        "",
        ["startDate:asc"],
        [`clientId==${clientId}`, `startDate=lt=${now}`, `endDate=gt=${now}`]
      );
      this.props.dataActions.getDataCount(CLIENT_ATTACHMENTS, [
        `clientAttachments.clientId==${clientId};clientAttachmentsUnread.userId==${userId}`,
      ]);

      const model = {
        path: `/v1/clients/${clientId}/get-last-generated-member`,
      };

      this.props.dataActions.simplePost({ model }).then((data) => {
        this.props.clientActions.setLastGeneratedMember(data.payload.data);
      });
      userHasAccess(INTERNAL_ROLES) && this.props.dataActions.getDataItem(CLIENT_CONFIGURATION, "").then((response) => {
        this.setState({ automaticIdentifierConfigurationTypes: response.payload.data.clients.memberIdentifier });
      });
      // Fetch MINT status once at the client level and store in Redux for use across all child components
      userHasAccess(INTERNAL_ROLES) && simpleFetch("get", IS_MINT_ENABLED.path, {}).then((res) => {
        this.props.clientActions.setMintEnabledStatus(res.isEnabled);
      }).catch(() => {
        // If MINT status fetch fails, default to disabled
        this.props.clientActions.setMintEnabledStatus(false);
      });
    } else {
        toast(
          <FormattedMessage id="clients.invalidClientIdType" />,
          {
            type: toast.TYPE.ERROR,
            autoClose: false,
          }
        );
    }
  }

  archiveButtonEdit(allowSubmission) {
    this.setState({ submitData: !allowSubmission });
  }

  updateTransition(value) {
    this.setState({ clientInTransition: value });
  }

  refetchIsTransition() {
    isClientInTransition(this.props.clientId).then((res) => {
      /** Response is either true or false. */
      this.updateTransition(res);
    });
  }

  createClientNote(fields) {
    const values = {
      createdByUserId: fields.userId,
      description: fields.description,
      attachments: fields.followUpAttachments,
      followUpGuid: fields.followUpGuid,
      followUpOriginType: fields.followUpOriginType,
      followUpStatusType: "OPEN",
      followUpType: fields.followUpType,
      clientId: fields.clientId,
      transactionAmount: fields.transactionAmount || null,
      followUpCategoryId: fields.followUpCategoryId,
      memberId: fields.memberId,
      memberIdentifier: fields.memberIdentifier,
    };
    if (fields.priority) {
      values.priority = fields.priority;
      values.deadline = fields.deadline;
    }
    if (fields.generateFollowUp) {
      switch (fields.followUpType) {
        case INCIDENT.value: {
          values.incidentType = fields.subType;
          break;
        }
        case SPECIAL_MODIFICATION.value: {
          values.specialModificationType = fields.subType;
          break;
        }
      }
      values.recipientUserId = fields.recipientUserId;
    }
    this.props.dataActions
      .createDataItem(CLIENT_FOLLOW_UPS, values)
      .then(() => {
        this.getFollowUps()
          .then(() => {
            this.props.dataActions.getDataCount(
              CLIENT_FOLLOW_UPS,
              [
                `followUps.clientId==${this.props.clientId};followUps.recipientUserId!=null;followUps.followUpStatusType==OPEN;followUps.followUpOriginType!=SYSTEM`,
              ],
              "MEMBER"
            );
          });
      });
  }

  updateClientInfo() {
    /**
     * Function that is called to update the client information.
     */
    this.props.dataActions.getDataItem(CLIENT_V2, this.props.clientId);
  }

  handleServerError(serverMessage, actionType, userLanguage) {
    const errorCodesToString = getParsedErrors(
      serverMessage,
      userLanguage,
      this.state.errorPath
    );
    this.setState({
      showModal: true,
      errorsString: errorCodesToString,
      actionType: actionType,
    });
  }

  showSingleModalError(errorCode, userLanguage, actionType) {
    const serverMessage = getSingleErrorFromCode(
      errorCode,
      userLanguage,
      this.state.errorPath
    );
    this.setState({
      showModal: true,
      errorsString: serverMessage,
      actionType: actionType,
    });
  }

  close() {
    this.setState({ showModal: false, errorsString: "", actionType: "" });
  }

  clearContactInfo(values) {
    /*** Clears contact info before patching it ***/
    delete values.accountManagerId;
    delete values.serviceRepresentativeId;
    delete values.contactFirstName;
    delete values.contactLastName;
    delete values.contactJobPosition;
    delete values.contactPhoneNumber;
    delete values.contactEmailAddress;
    delete values.contactLanguageCode;
    delete values.adminContactFirstName;
    delete values.adminContactLastName;
    delete values.adminContactJobPosition;
    delete values.adminContactPhoneNumber;
    delete values.adminContactEmailAddress;
    delete values.adminContactLanguageCode;
    delete values.adminContactPhoneNumberExtension;
    delete values.contactPhoneNumberExtension;
  }

  updateClient(values) {
    /**
      * Function that will update a client (from client modal).
      */
    const { dataActions } = this.props;

    const majorFixedFee = values.majorFixedFee || 0;
    const majorVariableFee = values.majorVariableFee || 0;

    const userLanguage = localStorage.getItem("languageCode") || EN.value;
    const translationFile = userLanguage == EN.value ? enCA : frCA;
    this.clearContactInfo(values);

    const valToUpdate = cleanBrokerRelationshipsFromForm({
      ...values,
      majorFixedFee,
      majorVariableFee,
    });

    return clientActions.updateClientAndValidate(
      dataActions,
      valToUpdate,
      this.updateClientInfo,
      this.handleServerError,
      translationFile.validation.clientFieldValidation.modifyAction,
      this.showSingleModalError,
      userLanguage
    );
  }

  updateClientNote(values) {
    let memberId = values.memberId; /** Can be int in some cases. */
    if (typeof memberId == "string") {
      memberId = values.memberId.trim() ? values.memberId : null;
    }
    /** Need to be set to null cause db wont accept string for int field. */
    const dataToUpdate = {
      attachments: values.followUpAttachments,
      followUpId: values.followUpId,
      followUpType: values.followUpType,
      description: values.description,
      transactionAmount: values.transactionAmount || null,
      recipientUserId: values.recipientUserId,
      followUpCategoryId: values.followUpCategoryId,
      clientId: values.clientId,
      memberId: memberId,
      memberIdentifier: values.memberIdentifier,
    };
    if (values.priority) {
      dataToUpdate.priority = values.priority;
      dataToUpdate.deadline = values.deadline;
    }
    if (!values.clientId) {
      dataToUpdate.clientName = values.clientName;
      dataToUpdate.contactPhoneNumber = values.contactPhoneNumber;
      dataToUpdate.contactPhoneNumber = values.contactPhoneNumber;
    }
    if (values.generateFollowUp) {
      switch (values.followUpType) {
        case INCIDENT.value: {
          dataToUpdate.incidentType = values.subType;
          dataToUpdate.specialModificationType = null;
          break;
        }
        case SPECIAL_MODIFICATION.value: {
          dataToUpdate.specialModificationType = values.subType;
          dataToUpdate.incidentType = null;
          break;
        }
      }
    }
    if (values.closeFollowUp) {
      dataToUpdate.followUpStatusType = "CLOSED";
      dataToUpdate.closedDate = new Date();
      dataToUpdate.closedByUserId = this.props.auth.user.userId;
      dataToUpdate.closedDescription = values.closedDescription;
    } else {
      dataToUpdate.followUpStatusType = "OPEN";
      dataToUpdate.closedDate = null;
      dataToUpdate.closedByUserId = null;
      dataToUpdate.closedDescription = null;
    }
    return this.props.dataActions
      .updateDataItem(CLIENT_FOLLOW_UPS, dataToUpdate)
      .then(() => {
        this.getFollowUps()
          .then(() => {
            this.props.dataActions.getDataCount(
              CLIENT_FOLLOW_UPS,
              [
                `followUps.clientId==${this.props.clientId};followUps.recipientUserId!=null;followUps.followUpStatusType==OPEN;followUps.followUpOriginType!=SYSTEM`,
              ],
              "MEMBER"
            );
          });
      });
  }

  render() {
    const {
      activeBillingPeriods,
      auth,
      children,
      client,
      clientAttachmentsCount,
      followUps,
      followUpsCount,
      blocs,
      memberId,
    } = this.props;

    const { clientInTransition } = this.state;

    if (memberId) {
      return <div className="full-height">{children}</div>;
    }
    if (this.state.unauthorized) {
      return <i className="hidden" />;
    }
    return (
      <div className="full-height">
        {client ? (
          <div className="full-height">
            <ClientHeader
              client={client}
              blocs={blocs}
              clientInTransition={clientInTransition}
              activeBillingPeriods={activeBillingPeriods}
              archiveButton={this.archiveButtonEdit}
              onSubmit={this.updateClient}
              allowSubmission={this.state.submitData}
              updateClientInfo={this.updateClientInfo}
              updateTransition={this.updateTransition}
              changeFieldValue={this.props.changeFieldValue}
              refetchIsTransition={this.refetchIsTransition}
              automaticIdentifierConfigurationTypes={this.state.automaticIdentifierConfigurationTypes}
            />
            <div className="sub-container">
              <ClientNav
                client={client}
                clientId={client.clientId}
                clientAttachmentsCount={clientAttachmentsCount}
                clientBillingPeriodType={client.clientBillingPeriodType}
                isMintEnabled={this.props.isMintEnabled}
              />
              {children}
            </div>
            <Render for={AGENT}>
              <FollowUps
                type="client"
                createNote={this.createClientNote.bind(this)}
                count={followUpsCount}
                auth={auth}
                client={client}
                followUps={followUps}
                updateNote={this.updateClientNote}
                needsActiveProperty={true}
              />
            </Render>
            {renderErrorModal(
              this.state.showModal,
              this.close,
              this.state.errorsString,
              "validation.clientFieldValidation.generalErrorMessage",
              this.state.actionType
            )}
          </div>
        ) : (
          <RectangleSpinner className="client-page-spinner" />
        )}
      </div>
    );
  }
}

ClientPage.propTypes = {
  activeBillingPeriods: PropTypes.array,
  auth: PropTypes.object,
  children: PropTypes.node,
  client: PropTypes.object,
  clientAttachmentsCount: PropTypes.number,
  clientId: PropTypes.string,
  dataActions: PropTypes.object,
  followUps: PropTypes.array,
  followUpsCount: PropTypes.number,
  blocs: PropTypes.array,
  memberId: PropTypes.string,
  clientActions: PropTypes.object,
  changeFieldValue: PropTypes.func.isRequired,
  isMintEnabled: PropTypes.bool,
};

function mapStateToProps(state, ownProps) {
  const clientAttachmentsCount = state.data.clientAttachmentsCount || {};
  const clients = state.data.clients || {};
  const followUps = state.data.followUps || {};
  const followUpsCount = state.data.clientFollowUpsCount || {};
  const activeBillingPeriods = state.data.activeBillingPeriods || {};
  const blocs = state.data.blocs || {};
  return {
    activeBillingPeriods: activeBillingPeriods.items || [],
    auth: state.auth,
    client: clients.active,
    clientAttachmentsCount: clientAttachmentsCount.count || 0,
    clientId: ownProps.params.clientId,
    followUps: followUps.items || null,
    followUpsCount: followUpsCount.count || 0,
    blocs: blocs.items || [],
    memberId: ownProps.params.memberId,
    path: ownProps.location.pathname,
    isMintEnabled: state.clientActions.isMintEnabled,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    changeFieldValue: bindActionCreators(changeFieldValue, dispatch),
    dataActions: bindActionCreators(dataActions, dispatch),
    clientActions: bindActionCreators(clientActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ClientPage);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/ClientPage.js